import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { ArrowLeft, Check, Play, Pause, RotateCcw, Trophy, Sparkles, Video } from 'lucide-react';
import { calculateDailyPoints, isMealsCompleted, isSleepCompleted } from '../utils/dailyProgressPoints';

interface WorkoutExecutionProps {
  templateId: string;
  onBack: () => void;
  onComplete: () => void;
}

interface Exercise {
  id: string;
  exercise_name: string;
  sets: number;
  reps: string;
  rest_seconds: number;
  tips: string | null;
  order: number;
  video_url?: string;
}

export default function WorkoutExecution({ templateId, onBack, onComplete }: WorkoutExecutionProps) {
  const { user } = useAuth();
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [isResting, setIsResting] = useState(false);
  const [restTimer, setRestTimer] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [completedSets, setCompletedSets] = useState<Record<string, number>>({});
  const [advancedMode, setAdvancedMode] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [totalSeconds, setTotalSeconds] = useState<number>(0);
  const BG_DARK = '#0D0D0D';

  useEffect(() => {
    const loadExercises = async () => {
      const { data } = await supabase
        .from('workout_exercises')
        .select('*')
        .eq('workout_template_id', templateId)
        .order('order', { ascending: true });

      if (data) {
        setExercises(data);
        const initialCompleted: Record<string, number> = {};
        data.forEach((ex: Exercise) => {
          initialCompleted[ex.id] = 0;
        });
        setCompletedSets(initialCompleted);
        setStartTime(Date.now());
      }
    };
    loadExercises();
  }, [templateId]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (timerRunning && restTimer > 0) {
      interval = setInterval(() => {
        setRestTimer((prev) => {
          if (prev <= 1) {
            setTimerRunning(false);
            setIsResting(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timerRunning, restTimer]);

  const currentExercise = exercises[currentExerciseIndex];

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTotalProgress = () => {
    const totalSets = exercises.reduce((sum, ex) => sum + ex.sets, 0);
    const completedTotal = Object.values(completedSets).reduce((sum, val) => sum + val, 0);
    return Math.round((completedTotal / totalSets) * 100);
  };

  const extractTechnique = (reps: string, tips?: string | null) => {
    const match = reps.match(/\(([^)]+)\)/);
    if (match) return match[1];
    if (tips) {
      const candidates = ['Dropset', 'Rest-Pause', 'Hash Pause', 'Superset', 'Giant Set'];
      const found = candidates.find((c) => tips.toLowerCase().includes(c.toLowerCase()));
      if (found) return found;
    }
    return null;
  };

  const handleCompleteWorkout = async () => {
    if (!user) return;

    const today = new Date().toISOString().split('T')[0];

    if (startTime) {
      setTotalSeconds(Math.floor((Date.now() - startTime) / 1000));
    }

    // Buscar progresso existente para preservar outros campos
    const { data: existing } = await supabase
      .from('daily_progress')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', today)
      .maybeSingle();

    // Preparar dados para cálculo de pontos
    const progressData = existing
      ? {
          workout_completed: true,
          sleep_hours: existing.sleep_hours,
          meals_logged: existing.meals_logged || 0
        }
      : {
          workout_completed: true,
          sleep_hours: null,
          meals_logged: 0
        };

    // Calcular pontos e flags de conclusão
    const mealsCompleted = isMealsCompleted(progressData.meals_logged);
    const sleepCompleted = isSleepCompleted(progressData.sleep_hours);
    const points = calculateDailyPoints(progressData);

    const updates = existing
      ? {
          ...existing,
          workout_completed: true,
          meals_completed: mealsCompleted,
          sleep_completed: sleepCompleted,
          points: points
        }
      : {
          user_id: user.id,
          date: today,
          workout_completed: true,
          water_ml: 0,
          meals_logged: 0,
          meals_completed: mealsCompleted,
          sleep_completed: sleepCompleted,
          points: points
        };

    await supabase.from('daily_progress').upsert(
      updates,
      { onConflict: 'user_id,date' }
    );

    // Disparar evento para atualizar dashboard e pontuação ofensiva
    window.dispatchEvent(new CustomEvent('dailyProgressUpdated'));

    onComplete();
  };

  const handleCompleteSet = () => {
    if (!currentExercise) return;

    const newCompleted = { ...completedSets };
    newCompleted[currentExercise.id] = currentSet;
    setCompletedSets(newCompleted);

    if (currentSet < currentExercise.sets) {
      setCurrentSet(currentSet + 1);
      setRestTimer(currentExercise.rest_seconds);
      setIsResting(true);
      setTimerRunning(true);
    } else {
      if (currentExerciseIndex < exercises.length - 1) {
        setCurrentExerciseIndex(currentExerciseIndex + 1);
        setCurrentSet(1);
        setIsResting(false);
        setRestTimer(0);
        setTimerRunning(false);
      } else {
        handleCompleteWorkout();
      }
    }
    if (navigator && 'vibrate' in navigator) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      try { (navigator as any).vibrate(10); } catch { /* ignore */ }
    }
  };

  const handleSkipRest = () => {
    setTimerRunning(false);
    setIsResting(false);
    setRestTimer(0);
  };

  const handlePauseResume = () => {
    setTimerRunning(!timerRunning);
  };

  if (!currentExercise) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-20" style={{ backgroundColor: BG_DARK }}>
        <div className="text-center">
          <Trophy className="w-24 h-24 text-white mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-2">Treino Concluído!</h2>
          <p className="text-[#B0B0B0] mb-6">Parabéns! Você completou todos os exercícios.</p>
          <div className="max-w-md mx-auto text-left rounded-2xl p-6 bg-[#141414] border border-white/10 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[#B0B0B0]">Exercícios concluídos</div>
                <div className="text-white font-semibold">{exercises.length}</div>
              </div>
              <div>
                <div className="text-[#B0B0B0]">Tempo total</div>
                <div className="text-white font-semibold">{Math.floor(totalSeconds / 60)} min</div>
              </div>
              <div>
                <div className="text-[#B0B0B0]">Séries concluídas</div>
                <div className="text-white font-semibold">{Object.values(completedSets).reduce((a, b) => a + b, 0)}</div>
              </div>
              <div>
                <div className="text-[#B0B0B0]">Consistência semanal</div>
                <div className="text-white font-semibold">Atualizado no Dashboard</div>
              </div>
            </div>
            <p className="text-xs text-[#7A7A7A] mt-4">“O shape não é pra quem tenta. É pra quem continua mesmo cansado.”</p>
          </div>
          <button
            onClick={onBack}
            className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition border border-white/20"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: BG_DARK }}>
      <div className="mx-auto px-4 py-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#B0B0B0] hover:text-white transition"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Sair</span>
          </button>
          <div className="text-[#B0B0B0] text-sm font-medium">
            {currentExerciseIndex + 1} / {exercises.length}
          </div>
        </div>

        <div className="mb-4">
          <div className="w-full bg-white/5 rounded-full h-2 mb-2">
            <div
              className="bg-white/20 h-2 rounded-full transition-all duration-500"
              style={{ width: `${getTotalProgress()}%` }}
            />
          </div>
          <p className="text-[#7A7A7A] text-xs text-right">{getTotalProgress()}%</p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="text-[#B0B0B0] text-xs">Aperfeiçoamento técnico</div>
          <button
            onClick={() => setAdvancedMode(!advancedMode)}
            className={`text-xs px-3 py-1 rounded-lg border ${advancedMode ? 'border-white/30 bg-white/10 text-white' : 'border-white/10 text-[#B0B0B0]'}`}
          >
            <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />Modo avançado</span>
          </button>
        </div>

        {isResting ? (
          <div className="bg-gray-800 rounded-2xl p-8 mb-6 text-center">
            <RotateCcw className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-white mb-2">Descansando</h2>
            <p className="text-gray-400 mb-6">Próxima série em breve</p>
            <div className="text-6xl font-bold text-blue-500 mb-6">{formatTime(restTimer)}</div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handlePauseResume}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition flex items-center gap-2"
              >
                {timerRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                {timerRunning ? 'Pausar' : 'Retomar'}
              </button>
              <button
                onClick={handleSkipRest}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition"
              >
                Pular Descanso
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl p-8 mb-6 bg-[#141414] border border-white/10">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">{currentExercise.exercise_name}</h2>
              <p className="text-[#B0B0B0]">
                Série {currentSet} de {currentExercise.sets}
              </p>
            </div>
            {/* Vídeo de execução */}
            {currentExercise.video_url ? (
              <div className="rounded-xl overflow-hidden mb-6 border border-white/10">
                <video
                  src={currentExercise.video_url}
                  className="w-full h-56 object-cover"
                  muted
                  loop
                  playsInline
                  controls
                />
              </div>
            ) : (
              <div className="rounded-xl p-4 mb-6 bg-white/5 border border-white/10 flex items-center gap-2 text-[#B0B0B0]">
                <Video className="w-4 h-4" />
                Vídeo de execução em breve
              </div>
            )}

            <div className="rounded-xl p-6 mb-6 bg-white/5 border border-white/10">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-white mb-1">{currentExercise.sets}</div>
                  <div className="text-[#B0B0B0] text-sm">Séries</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white mb-1">{currentExercise.reps}</div>
                  <div className="text-[#B0B0B0] text-sm">Reps</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white mb-1">{currentExercise.rest_seconds}s</div>
                  <div className="text-[#B0B0B0] text-sm">Descanso</div>
                </div>
              </div>
            </div>

            {(advancedMode || currentExercise.tips) && (
              <div className="rounded-xl p-4 mb-6 bg-white/5 border border-white/10">
                {advancedMode && (
                  <div className="text-xs text-white mb-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 border border-white/20">
                    <Sparkles className="w-3 h-3" />
                    Técnica sugerida: {extractTechnique(currentExercise.reps, currentExercise.tips) || 'Hash Pause / Rest-Pause'}
                  </div>
                )}
                {currentExercise.tips && (
                  <p className="text-sm text-[#B0B0B0] leading-relaxed">{currentExercise.tips}</p>
                )}
              </div>
            )}

            <button
              onClick={handleCompleteSet}
              className="w-full py-5 bg-white/10 hover:bg-white/20 text-white font-bold text-lg rounded-xl transition flex items-center justify-center gap-2 border border-white/20"
            >
              <Check className="w-6 h-6" />
              Concluir Série
            </button>
          </div>
        )}

        <div className="rounded-xl p-4 bg-[#141414] border border-white/10">
          <h3 className="text-sm font-semibold text-[#B0B0B0] mb-3">Progresso dos Exercícios</h3>
          <div className="space-y-2">
            {exercises.map((exercise, index) => {
              const completed = completedSets[exercise.id] || 0;
              const percentage = (completed / exercise.sets) * 100;
              const isCurrent = index === currentExerciseIndex;

              return (
                <div key={exercise.id} className={`p-3 rounded-lg ${isCurrent ? 'bg-white/5 border border-white/30' : 'bg-[#101010] border border-white/5'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${isCurrent ? 'text-white' : 'text-[#B0B0B0]'}`}>
                      {exercise.exercise_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {completed}/{exercise.sets}
                    </span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${isCurrent ? 'bg-white/30' : 'bg-white/10'}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
